import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import {
  EstadosAgendamientoEnumChoice,
  EstadosLlamadaEnumChoice,
  ROUTER_PATHS,
  toHour,
  toHours,
  TrazabilidadAccionChoiceEnum,
  useLoaders,
} from "@/shared";
import {
  AproveRecoordinacionData,
  useUpdateAgendamiento,
  useUpdateOrdenTrabajo,
} from "@/shared/actions/erp";
import {
  CustomButtunsRejectConfirm,
  SingleFormBoxScene,
} from "@/shared/components/ui";
import {
  Agendamiento,
  AlertSeverityEnum,
  CacheKeyCalendarioENUM,
  ErrorMessagesForm,
  EstadoBitacoraEnum,
  OrdenTrabajo,
} from "@/shared/interfaces";
import {
  agendamientoFormSchemaMinified,
  calcTrazavilidadVendedor,
  convertirFormatoHoraArray,
  convertirHoraADecimalArray,
  handleErrorForm,
  hasPermission,
} from "@/shared/utils";
import { useUiStore } from "@/store/ui";
import RejectAgendaModal from "../forms/RejectAgendaModal";
import { useDiccionarioMotivo } from "@/store/erp/diccionario_motivo/diccionario_motivo.store";
import { SaveFormDataAgendamiento } from "../../interface/SaveFormDataAgendamiento.interface";
import AgendamientoSinglePage from "../agendamiento_single_page/AgendamientoSinglePage";
import { PermissionsEnum } from "@/auth/interfaces";
import useSelectDateTimerPreScheduling, { DepartamentoSelectScheduling } from "@/erp/sale/component/calendario-preagendamiento-venta/useSelectDateTimerPreScheduling.store";
import { useAuthStore } from "@/store/auth";
export interface SaveAgendamientoPyLProps {
  title: string;
  agendamiento?: Agendamiento;
  dataOT?: Partial<OrdenTrabajo>;
}


const SaveAgendamientoPyL: React.FC<SaveAgendamientoPyLProps> = ({
  title,
  agendamiento,
  dataOT
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const returnUrlAgendamientosPyLPage =
    ROUTER_PATHS.operaciones.agendamientoPyLNav;

  const [openRechazoModal, setOpenRechazoModal] = useState(false);

  const getFloat = useSelectDateTimerPreScheduling(s => s.getFloat);
  const setIsSave = useSelectDateTimerPreScheduling(s => s.setIsSave);
  const handleValidDateHour = useSelectDateTimerPreScheduling(s => s.handleValidDateHour);
  const blocked = useSelectDateTimerPreScheduling(s => s.blocked);
  const selectFloat = useSelectDateTimerPreScheduling(s => s.selectFloat);
  const isLoadingFloat = useSelectDateTimerPreScheduling(s => s.isLoadingFloat);
  const selectDate = useSelectDateTimerPreScheduling(s => s.selectDate);
  const slot = useSelectDateTimerPreScheduling(s => s.slot);
  const setPreventa = useSelectDateTimerPreScheduling(s => s.setPreventa);
  const unblockPlanification = useSelectDateTimerPreScheduling(s => s.unblockPlanification);
  const setConfigParams = useSelectDateTimerPreScheduling(s => s.setConfigParams);

  const isPyLAdmin = hasPermission(
    PermissionsEnum.pyl_administrator_permission
  );

  ///* local state --------------------
  // const [openRechazoModal, setOpenRechazoModal] = useState(false);
  const [shrink] = useState(true);

  ///* global state --------------------
  const setAlert = useUiStore((s) => s.setAlert);

  /* const {
    bloquear,
    fechaSelecionada,
    clickedIdHora,
    codigoFlota,
    horasSelect,
    setCountdownTimeOut,
    handleVerificarCache,
    setFechaHoraDisponible,
    setCodigoFlota,
    handleValidarFechaVendedor
  } = useCalendarioInstalacionPyl(); */

  const emitFinalizarVenta = useDiccionarioMotivo((s) => s.emitFinalizarVenta);

  /* const {
    getFlotasPreagendamiento,
    isLoadingFlotasPreagendamiento
  } =
    useFlotasStore(); */

  ///* form --------------------
  const form = useForm<SaveFormDataAgendamiento>({
    resolver: yupResolver(agendamientoFormSchemaMinified) as any,
  });

  const {
    handleSubmit,
    reset,
    formState: { isValid },
  } = form;

  ///* mutations --------------------
  const updateAgendamientoMutation =
    useUpdateAgendamiento<AproveRecoordinacionData>({});
  const updateOrdenTrabajoMutation =
    useUpdateOrdenTrabajo<Partial<OrdenTrabajo>>({
      enableRedirect: false,
      enableSuccessAlert: false
    });

  ///* handlers --------------------
  const authId = useAuthStore(s=>s.user)?.id

  const onSave = async (data: SaveFormDataAgendamiento) => {
    if (!isValid) return;

    setIsSave(true)

    /* if (selectDate == null || slot == null || selectFloat == null)
      return toast.error(
        "Selecionar un técnico con fecha y hora de instalación.",
      ); */

    if (
      data.estado_llamada === EstadosLlamadaEnumChoice.REALIZADA &&
      !data.isRejecting
    ) {
      const esValid = await handleValidDateHour(authId)
      if (!esValid) return;

    }

    ///* upd
    if (agendamiento && agendamiento?.id_agendamiento) {
      const resp = await updateAgendamientoMutation.mutateAsync({
        id: agendamiento.id_agendamiento!,
        data: {
          celular: data.celular!,
          celular_2: data.celular_2!,
          email: data.email!,
          direccion: data.direccion!,
          coordenadas: data.coordenadas!,

          // estado llamada ----
          estado_llamada: data.estado_llamada!,
          fecha_hora_llamada: dayjs().format(),
          nap: data.nap,
          distancia: data.distancia,
          observaciones: {
            ...agendamiento?.preventa_data?.observaciones,
            observacion_llamada: data.observacion_llamada!,
          },
          pais: data.pais!,
          ciudad: data.ciudad!,
          provincia: data.provincia!,
          zona: data.zona!,
          sector: data.sector!,
          persona_referencia_celular: data.persona_referencia_celular!,
          persona_referencia_nombre: data.persona_referencia_nombre!,
          persona_referencia_parentesco: data.persona_referencia_parentesco!,

          estado_agendamiento: form.getValues().isRejecting
            ? EstadosAgendamientoEnumChoice.PRERECHAZADO
            : EstadosAgendamientoEnumChoice.RECORDINADO,
          motivo_rechazo: data.motivo_rechazo || null,
          observacion_rechazo: data.observacion_rechazo || null,

          ///* Cambio de flota y hora de instalacion
          flota: blocked
            ? selectFloat!
            : data.flota,
          fecha_instalacion: blocked
            ? selectDate.format("YYYY-MM-DD")
            : data.fecha_instalacion,
          hora_instalacion: blocked
            ? toHour(slot!.hora)
            : data.hora_instalacion!,
          horas: convertirFormatoHoraArray(
            blocked
              ? toHours(slot!.hora)
              : convertirHoraADecimalArray(data.hora_instalacion)
          ),
          fecha_instalacion_original:
            agendamiento.preagendamiento_data?.fecha_instalacion!,
          hora_instalacion_original:
            agendamiento.preagendamiento_data?.hora_instalacion!,
        },
      }, {
        onSuccess: () => {
          // Solo actualizar la OT si realmente cargo. Sin este guard, cuando
          // dataOT es undefined (race condition al entrar y clickear rapido,
          // o agendamientos sin OT asociada) el PUT iba a
          // /api/v1/orden-trabajo/undefined/ y fallaba con 500.
          const otId = dataOT?.id_orden_trabajo;
          if (!otId) return;
          updateOrdenTrabajoMutation.mutate({
            id: otId,
            data: {
              flota: blocked
                ? selectFloat!
                : data.flota,
              fecha_instalacion: blocked
                ? selectDate.format("YYYY-MM-DD")
                : data.fecha_instalacion,
              hora_instalacion: blocked
                ? toHour(slot!.hora)
                : data.hora_instalacion!,
            },
          });
        },
      });

      if (resp.code === 200) {

        ///* Cambio de Estado de Agendamiento a Recoordinación, cierre de bitacora automatico
        emitFinalizarVenta({
          numero_referencia_venta: agendamiento.preagendamiento_data?.id_preagendamiento!,
          estado_venta: EstadoBitacoraEnum.FINALIZADO,
        });

        /* setCountdownTimeOut(
          0,
          CacheKeyCalendarioENUM.KEY_CACHE_PREAGENDAMIENTO_PYL,
          false,
          false
        ); */
        queryClient.removeQueries({ queryKey: ['agendamientos'] });
        navigate(returnUrlAgendamientosPyLPage, { state: { tabIndex: 3 } });
      }
      return;
    }
  };

  ///* effects --------------------
  useEffect(() => {
    if (!agendamiento?.id_agendamiento) return;

    const {
      preventa_data,
      prospecto_data,
      pais_data,
      provincia_data,
      ciudad_data,
      zona_data,
      sector_data,
      plan_data,
      flota_data,
      trazo_venta_data,
      observaciones,
      ...rest
    } = agendamiento;
    reset({
      ...prospecto_data,
      ...preventa_data,
      ...rest,
      hora_instalacion: rest.hora_instalacion,
      fecha_instalacion: rest.fecha_instalacion,
      nacionalidad: pais_data?.nacionalidad!,
      observacion_1: preventa_data?.observaciones.observacion_1 || "",
      observacion_2: preventa_data?.observaciones.observacion_2 || "",

      vendedor_name: calcTrazavilidadVendedor(
        trazo_venta_data || [],
        TrazabilidadAccionChoiceEnum.CREACION,
        "user"
      ),
      es_referido: !!preventa_data?.tipo_referido ? "SI" : "NO",
      planLabel: plan_data?.nombre!,
      velocidad_internet: preventa_data?.velocidad
        ? `${preventa_data?.velocidad} ${plan_data?.unidad}`
        : "",

      isRejecting: false,

      flota: rest.flota || "",
      flotaName: flota_data?.nombre || "",
      flotaRaw: flota_data,
      rawNap: rest.nap_data || {},
      estado_llamada: rest.estado_llamada || "",
      observacion_llamada: observaciones?.observacion_llamada || "",
    } as unknown as SaveFormDataAgendamiento);
  }, [agendamiento?.id_agendamiento, reset]);

  ///* form --------------------

  // cargar planificador
  useEffect(() => {

    setConfigParams(
     {
      time: 60 * 1000,
      tiempo :CacheKeyCalendarioENUM.TIEMPO_LIMITE_PYL,
      motivo: CacheKeyCalendarioENUM.MENSAJE_BLOQUEO_PYL,
      departamento: DepartamentoSelectScheduling.PYL
     }
    )

    if (!agendamiento) return;


    (async () => {
      await Promise.all([
        getFloat({
          estado: true,
          zonas: agendamiento?.preventa_data?.zona,
          page_size: 100,
        }),
        setPreventa(agendamiento?.preventa_data?.id_preventa!)
      ]);
    })();


    return () => {
      unblockPlanification()
    }
  }, [agendamiento?.id_agendamiento])


  useLoaders(
    isLoadingFloat
  )

  return (
    <SingleFormBoxScene
      py={0}
      titlePage={title}
      showCustomBtns={true}
      showActionBtns={false}
      num_referencia={agendamiento?.prospecto_data?.razon_social}
      customBtns={
        <CustomButtunsRejectConfirm
          onCancel={() => {
            navigate(returnUrlAgendamientosPyLPage);
          }}
          onConfirm={() => {
            handleSubmit(onSave, (err) => {
              if (err?.estado_llamada || err?.observacion_llamada)
                return setAlert(
                  true,
                  "El estado de llamada y su observación son requeridos",
                  AlertSeverityEnum.error
                );
              handleErrorForm(err as ErrorMessagesForm);
            })();
          }}
          confirmTextBtn="Recoordinar"
          rejectTextBtn="Enviar a Gestión Ventas"
          showRejectBtn={isPyLAdmin}
          // onReject={() => null}
          // form.setValue('isRejecting', true)
          onReject={() => {
            setOpenRechazoModal(true);
            form.reset({
              ...form.getValues(),
              isRejecting: true,
            });
          }}
          sxContainer={{
            pt: 5,
          }}
        />
      }

    >


      <>
        <AgendamientoSinglePage
          form={form}
          shrink={shrink}
          agendamiento={agendamiento!}
        />
      </>

      {/* =================== DIALOG =================== */}
      <RejectAgendaModal
        open={openRechazoModal}
        onClose={() => {
          setOpenRechazoModal(false);
        }}
        agendamiento={agendamiento!}
        form={form}
        onConfirm={() => {
          handleSubmit(onSave, (err) => {
            if (err)
              return setAlert(
                true,
                "Revise que todos los campos se encuentren correctos.",
                AlertSeverityEnum.error
              );
            handleErrorForm(err);
          })();
        }}
      />
    </SingleFormBoxScene>
  );
};

export default SaveAgendamientoPyL;
